import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BudgetApiClient } from '../api/client';
import type { BudgetTableEntry, FilterState } from '../types';

export interface UseBudgetEntriesResult {
  entries: BudgetTableEntry[];
  totalCount: number;
  /** Total number of pages for the current filter set. */
  pageCount: number;
  /** 0-based index of the page currently displayed. */
  currentPage: number;
  /** Fetch and display the given 0-based page. */
  goToPage: (page: number) => void;
  isLoading: boolean;
  error: string | null;
  year: number;
  hasIst: boolean;
}

const PAGE_SIZE = 20;

export function useBudgetEntries(
  apiClient: BudgetApiClient,
  filters: FilterState,
  drillDownType: string | null,
  drillDownSubType: string | null,
  selectedYear: number
): UseBudgetEntriesResult {
  const [entries, setEntries] = useState<BudgetTableEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState<number>(selectedYear);
  const [hasIst, setHasIst] = useState(false);
  const requestIdRef = useRef(0);

  // Drill-down overrides user filter selections for expenditure type.
  // Memoized so the filter-change effect below only re-runs on actual content changes,
  // not on every parent re-render. Replaces the previous useRef-update pattern,
  // which silently went stale if a future dep was added to the constructor without
  // being added to the ref-updating effect.
  const effectiveFilters: FilterState = useMemo(() => ({
    ...filters,
    expenditureType: drillDownType || filters.expenditureType,
    subExpType: drillDownType
      ? (drillDownSubType !== null && drillDownSubType !== undefined
          ? `${drillDownType}|${drillDownSubType}`
          : null)
      : filters.subExpType,
  }), [filters, drillDownType, drillDownSubType]);

  // Fetch a single page and REPLACE the displayed rows (page-based pagination,
  // not the old append-on-scroll model). A concurrent fetch (filter change or
  // another page click) increments requestIdRef, which cancels any in-flight
  // response on arrival. Identity changes whenever the filters/year change, so
  // the reset effect below re-runs exactly when the query changes.
  const fetchPage = useCallback((page: number) => {
    const currentRequestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    apiClient.getBudgetEntries(effectiveFilters, { limit: PAGE_SIZE, offset: page * PAGE_SIZE }, selectedYear)
      .then(result => {
        if (currentRequestId !== requestIdRef.current) return;
        setEntries(result.entries);
        setTotalCount(result.totalCount);
        setYear(result.year);
        setHasIst(result.hasIst);
        setCurrentPage(page);
      })
      .catch(err => {
        if (currentRequestId !== requestIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to fetch entries');
      })
      .finally(() => {
        if (currentRequestId === requestIdRef.current) setIsLoading(false);
      });
  }, [apiClient, effectiveFilters, selectedYear]);

  // Any time the query (filters/year) changes, snap back to the first page.
  useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const goToPage = useCallback((page: number) => {
    const clamped = Math.max(0, Math.min(page, pageCount - 1));
    if (clamped === currentPage || isLoading) return;
    fetchPage(clamped);
  }, [fetchPage, pageCount, currentPage, isLoading]);

  return {
    entries,
    totalCount,
    pageCount,
    currentPage,
    goToPage,
    isLoading,
    error,
    year,
    hasIst,
  };
}
